import type { ReactNode } from "react";

import type { RemoteErrorStatus } from "src/api/remoteCollectionState";
import type { RemoteEntityState } from "src/api/remoteEntityState";
import { Loading } from "src/components/utils/Loading";
import { RemoteErrorAlert } from "./RemoteErrorAlert";

export type RemoteEntityErrorContent = Record<RemoteErrorStatus, ReactNode>;

type RemoteEntityStateBoundaryProps<T> = {
    children: ReactNode;
    errorContent: RemoteEntityErrorContent;
    forbiddenContent?: ReactNode;
    onRetry: () => void | Promise<unknown>;
    state: RemoteEntityState<T>;
};

export const RemoteEntityStateBoundary = <T,>({
    children,
    errorContent,
    forbiddenContent,
    onRetry,
    state,
}: RemoteEntityStateBoundaryProps<T>) => {
    if (state.status === "success-value") {
        return (
            <>
                {state.refreshError !== undefined && (
                    <RemoteErrorAlert
                        message={errorContent[state.refreshError]}
                        onRetry={onRetry}
                        status={state.refreshError}
                    />
                )}
                {children}
            </>
        );
    }
    if (state.status === "loading") return <Loading />;
    if (state.status === "forbidden" && forbiddenContent !== undefined) return <>{forbiddenContent}</>;

    return <RemoteErrorAlert message={errorContent[state.status]} onRetry={onRetry} status={state.status} />;
};
